/* eslint-disable no-loop-func */
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import Chat from 'components/Chat'
import { AppSendMessageIcon } from 'components/Common/AppSvgIcons/AppSendMessageIcon'
import { AppPlayIcon } from 'components/Common/AppSvgIcons/AppPlayIcon'
import Avatar from 'appAssets/appImages/Avatar.png'
import { ReactComponent as QuickChatIcon } from 'assets/images/quick-chat-icon.svg'
import { ReactComponent as ArrowsPointingInIcon } from 'assets/images/arrows-pointing-in.svg'
import { ReactComponent as FolderIcon } from 'assets/images/folder.svg'
import { AppTypingIndicator } from 'components/AppOnboarding/AppChatbot/AppTypingIndicator/AppTypingIndicator.component'
import { RootState } from 'App'
import { saveUserFileAction } from 'store/actions/common'
import { getUsersAssessmentResponseByQuestionAction } from 'store/actions/owner/initialAssessment'
import { getChatUsersAction, chatUnreadMessageCountAction } from 'store/actions/user'
import { getAuthToken } from 'lib/auth'
import {
  useGetChatSessionsQuery,
  useSummarySessionTitleMutation,
  useCreateFlowChatSessionMutation,
  useGetBusinessProfileQuery,
  OnboardingFlowType,
  OnboardingFlowStepTypes,
  OnboardingFlowStepStatus,
  Tool,
  useGetAllToolsQuery,
} from '__generated__/api-types-and-hooks'
import { useInfiniteGetMessagesFromBotQuery } from '__generated__/infinite-api-types-and-hooks'
import { LoadingIndicator } from 'stream-chat-react'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppPaperClipIcon } from 'components/Common/AppSvgIcons/AppPaperClipIcon'
import CircleNotchIcon from 'components/Common/SvgIcons/CircleNotchIcon'
import 'components/AppOnboarding/AppChatbot/AppChatBotFlows/styles.css'
import { getInitials } from 'components/Chat/helper'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import { AppFileUploadField } from 'components/Common/AppFileUploadField/AppFileUploadField.component'
import {
  AppDynamicChatMessage,
  UIComponentProps,
} from 'components/AppOnboarding/AppChatbot/AppDynamicChatMessage/AppDynamicChatMessage.component'
import { useForm } from 'react-hook-form'
import { getTenantId } from 'utils/getTenantId'
import { AppAudioRecorder } from 'components/Common/AppAudioRecorder/AppAudioRecorder.component'
import { AppTextToSpeech, useTextToSpeech } from 'components/Common/AppTextToSpeech/AppTextToSpeech.component'
import { AppVoiceIndicator } from 'components/Common/AppVoiceIndicator/AppVoiceIndicator.component'
import { 
  extractTextForTTS, 
  isAudioRecordingSupported, 
  isSpeechSynthesisSupported 
} from 'utils/audioUtils'
import { AppTask } from 'components/AppKanbanBoard/AppTask/AppTask.component'
import { AppTaskCreationConfirmation } from 'components/AppKanbanBoard/AppTaskCreationConfirmation/AppTaskCreationConfirmation.component'
import { UserTask, UserTaskSource } from '__generated__/api-types-and-hooks'
import { useGetBusinessProfile } from 'appHooks/useGetBusinessProfile'
import AppCrossIcon from 'components/Common/AppSvgIcons/AppModalCrossIcon'
import { AppToolTip } from 'components/Common/AppToolTip/AppToolTip.component'
import { SafeMarkdown } from 'components/Common/SafeMarkdown/SafeMarkdown.component'
import { errorMessages } from 'components/AppOnboarding/AppChatbot/AppChatBotFlows/constants'

// Note: Chat context generation functions removed - play descriptions are now empty by default

interface Message {
  id: string
  sender: 'user' | 'bot'
  content: string | UIComponentProps
  timestamp: Date
  createdAt?: string
  isEdited?: boolean | null
  isVoiceMessage?: boolean
  voiceDuration?: number
}

interface StreamingResponse {
  type: 'session' | 'content' | 'tool_call' | 'complete' | 'error'
  sessionId?: string
  content?: string
  toolName?: string
  fullResponse?: string
  error?: string
  transcript?: string
  timestamp: string
}

interface AppFloatingChatProps {
  sessionId?: string
  isVisible?: boolean
  onToggle?: (isOpen: boolean) => void
  user?: any
  chatUsers?: any
  token?: string
  usersAssessmentResponseByQuestion?: any
  chatUnreadMessageCount?: number
  saveUserFileAction?: any
  getUsersAssessmentResponseByQuestionAction?: any
  getChatUsersAction?: any
  chatUnreadMessageCountAction?: any
  uploadFileAction?: any
  uploadFileLoading?: boolean
  uploadedFile?: {
    id: string
    name: string
    url: string
  }
}

const AppFloatingChatComponent: React.FC<AppFloatingChatProps> = ({
  sessionId,
  isVisible = false,
  onToggle,
  user,
  chatUsers,
  token,
  usersAssessmentResponseByQuestion,
  chatUnreadMessageCount,
  saveUserFileAction,
  getUsersAssessmentResponseByQuestionAction,
  getChatUsersAction,
  chatUnreadMessageCountAction,
  uploadFileAction,
  uploadFileLoading,
  uploadedFile,
}) => {
  const [isOpen, setIsOpen] = useState(isVisible)
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [activeTab, setActiveTab] = useState<'coach' | 'chat'>('coach')
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null)
  const [showSessionList, setShowSessionList] = useState(false)
  const [userMessageCount, setUserMessageCount] = useState(0)
  const [isUserScrolling, setIsUserScrolling] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const floatingChatRef = useRef<HTMLDivElement>(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  // Form-related state
  const [processing, setProcessing] = useState(false)
  const [currentStep, setCurrentStep] = useState<{
    stepType?: OnboardingFlowStepTypes
    stepStatus?: OnboardingFlowStepStatus
  }>({})
  const [queuedMessages, setQueuedMessages] = useState<any[]>([])
  const [isUpload, setIsUpload] = useState(false)
  const [uploadedFormFile, setUploadedFormFile] = useState<File[]>([])
  const [startUpload, setStartUpload] = useState(false)
  
  // Audio-related state
  const [audioSupported, setAudioSupported] = useState(false)
  const [ttsSupported, setTtsSupported] = useState(false)
  

  // Play creation modal state
  const [showCreatePlayModal, setShowCreatePlayModal] = useState(false)
  const [showPlaySuccessModal, setShowPlaySuccessModal] = useState(false)
  const [showPlayErrorModal, setShowPlayErrorModal] = useState(false)
  const [createdUserTask, setCreatedUserTask] = useState<UserTask | null>(null)

  // Replace pendingFiles and pendingFilePreviews with a single state
  const [pendingFiles, setPendingFiles] = useState<
    {
      file: File
      previewUrl: string
      status: 'pending' | 'uploading' | 'uploaded'
      uploadedUrl?: string
      uploadedName?: string
    }[]
  >([])

  // Add useForm hook for form handling
  const { handleSubmit, setValue, resetField } = useForm()

  // Text-to-speech hook
  const { speak: speakText } = useTextToSpeech()

  // Check audio support on mount
  useEffect(() => {
    setAudioSupported(isAudioRecordingSupported())
    setTtsSupported(isSpeechSynthesisSupported())
  }, [])

  // GraphQL hooks
  const { data: chatSessionsData, refetch: refetchChatSessions } = useGetChatSessionsQuery({
    input: {
      userId: user?.id,
      flowType: OnboardingFlowType.GeneralChat,
    },
  })

  const { mutate: summarySessionTitleMutate } = useSummarySessionTitleMutation({
    onSuccess: () => {
      refetchChatSessions()
    },
  })

  const { mutate: createFlowChatSessionMutate } = useCreateFlowChatSessionMutation({
    onSuccess: () => {
      refetchChatSessions()
    },
  })

  // Add tools query for form handling
  const { data: toolsData, refetch: refetchTools } = useGetAllToolsQuery(
    {
      tenantId: getTenantId(),
    },
    {
      refetchOnWindowFocus: false,
    }
  )
  const tools = (toolsData?.getAllTools?.data as Tool[]) || []

  // Fetch business profile for logo
  const { data: businessData } = useGetBusinessProfileQuery(
    {
      id: user?.id,
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!user?.id,
    }
  )

  // Fetch assignee options and advisor data using the business profile hook
  const { assigneeOptions, advisorsData } = useGetBusinessProfile({
    paramUserId: user?.id,
    enabled: !!user?.id,
  })

  // Get logo from businessData (same logic as AppChatBotFlows)
  const logo = React.useMemo(
    () =>
      businessData?.getBusinessProfile?.logo?.url
        ? businessData?.getBusinessProfile?.logo?.url
        : businessData?.getBusinessProfile?.avatar ?? '',
    [businessData]
  )

  // Fetch the messages with pagination support
  const {
    data: messageHistoryData,
    fetchNextPage,
    hasNextPage,
    isLoading: isMessagesLoading,
    isFetchingNextPage,
    refetch: refetchMessages,
  } = useInfiniteGetMessagesFromBotQuery(
    {
      sessionId: currentSessionId ?? '',
    },
    {
      getNextPageParam: (lastPage: any) => {
        return lastPage.getMessagesFromBot.nextCursor
          ? { cursor: lastPage.getMessagesFromBot.nextCursor }
          : undefined
      },
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      enabled: !!currentSessionId, // Only fetch when we have a session ID
    }
  )

  // Convert message from GraphQL format to component format
  const convertMessage = useCallback(
    (message: {
      __typename?: 'ChatBotMessage'
      role: string
      content: string
      createdAt: string
      isEdited?: boolean | null
    }): Message => {
      // Check if content is JSON (for form messages)
      if (message?.content?.startsWith('{')) {
        try {
          const content = JSON.parse(message.content)
          return {
            id: `${message.role}-${message.createdAt}`,
            sender: message.role === 'user' ? 'user' : 'bot',
            content,
            timestamp: new Date(message.createdAt),
            createdAt: message.createdAt,
            isEdited: message.isEdited || false,
          }
        } catch (e) {
          console.error('Error parsing message content:', e)
        }
      }

      return {
        id: `${message.role}-${message.createdAt}`,
        sender: message.role === 'user' ? 'user' : 'bot',
        content: message.content ?? '',
        timestamp: new Date(message.createdAt),
        createdAt: message.createdAt,
        isEdited: message.isEdited || false,
      }
    },
    []
  )

  // Add messages from latest page
  const addMessagesFromLatestPage = useCallback(
    (
      pageMessages: {
        __typename?: 'ChatBotMessage'
        role: string
        content: string
        createdAt: string
      }[]
    ) => {
      const newMessages = pageMessages
        .filter((message) => message.content && message.content.trim() !== '') // Filter out empty messages from backend
        .map((message) => convertMessage(message))
        .reverse()
        .filter(Boolean)

      // Avoid adding duplicate messages by checking their createdAt and content
      setMessages((prevMessagesArray) => {
        const existingMessages = new Set(
          prevMessagesArray.map((msg) => `${msg.createdAt}-${msg.content}`)
        )
        const oldMessages =
          newMessages?.filter((msg) => {
            const key = `${msg?.createdAt}-${msg?.content}`
            return !existingMessages.has(key)
          }) ?? []
        return [...oldMessages, ...prevMessagesArray]
      })
    },
    [convertMessage]
  )

  // Track session ID changes to reset messages
  const prevSessionIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (prevSessionIdRef.current !== currentSessionId) {
      // If the sessionId has changed, reset messages and refetch
      setMessages([]) // Clear previous messages
      prevSessionIdRef.current = currentSessionId
    }
  }, [currentSessionId])

  // Handle message history loading
  useEffect(() => {
    if (!messageHistoryData || !currentSessionId) return

    const pageMessages = messageHistoryData.pages ?? []
    const lastPageMessages =
      pageMessages[pageMessages.length - 1]?.getMessagesFromBot.messages ?? []

    if (!messages.length && pageMessages.length > 1) {
      pageMessages.forEach((page: any) => {
        addMessagesFromLatestPage(page.getMessagesFromBot.messages ?? [])
      })
      return
    }
    if (lastPageMessages.length) {
      addMessagesFromLatestPage(lastPageMessages)
    }
  }, [messageHistoryData, currentSessionId, addMessagesFromLatestPage, messages.length])

  // Auto-scroll to bottom when messages change
  const scrollToBottom = useCallback(
    (force?: boolean) => {
      if (!chatContainerRef.current) return
      if (!isUserScrolling || force) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
      }
    },
    [isUserScrolling]
  )

  // Handle user scroll for pagination
  const handleUserScroll = () => {
    if (chatContainerRef.current) {
      const isAtBottom =
        chatContainerRef.current.scrollHeight - chatContainerRef.current.scrollTop ===
        chatContainerRef.current.clientHeight
      setIsUserScrolling(!isAtBottom)

      // Trigger loading more messages when the user scrolls to the top
      if (chatContainerRef.current.scrollTop === 0 && hasNextPage) {
        fetchNextPage()
      }
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping, scrollToBottom])

  // Initialize with greeting message for new sessions
  useEffect(() => {
    if (isOpen && messages.length === 0 && !currentSessionId) {
      setMessages([
        {
          id: 'greeting',
          sender: 'bot',
          content: "Hi, I'm Coach Logic! How can I assist you today?",
          timestamp: new Date(),
        },
      ])
    }
  }, [isOpen, messages.length, currentSessionId])

  const toggleChat = useCallback(() => {
    const newState = !isOpen
    setIsOpen(newState)
    // Reset expanded state when closing the chat
    if (!newState) {
      setIsExpanded(false)
    }
    onToggle?.(newState)
  }, [isOpen, onToggle])

  // Handle expand/collapse
  const toggleExpand = useCallback(() => {
    setIsExpanded(!isExpanded)
  }, [isExpanded])

  // Handle folder icon click
  const handleFolderClick = useCallback(() => {
    setShowSessionList(!showSessionList)
    if (!showSessionList) {
      refetchChatSessions()
    }
  }, [showSessionList, refetchChatSessions])

  // Handle session selection
  const handleSessionSelect = useCallback((session: any) => {
    setCurrentSessionId(session.id)
    setMessages([]) // Clear messages - they will be loaded by the message history effect
    setShowSessionList(false)
    setIsUserScrolling(false)

    // Reset message count for existing session (we don't know how many messages it has)
    // For existing sessions, we assume they might already have a title, so we mark them as titled
    setUserMessageCount(0)
  }, [])

  // Send an empty message to bot to trigger initial greeting
  const sendEmptyMessageToBot = useCallback(
    async (sessionId: string) => {
      if (!sessionId) return

      try {
        const streamChatbotUrl = process.env.REACT_APP_STREAM_CHATBOT_URL || ''
        const authToken = await getAuthToken()

        if (!authToken) {
          throw new Error('Authentication token is required')
        }

        console.log('Sending empty message to initialize chat session:', sessionId)

        const response = await fetch(streamChatbotUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken,
          },
          body: JSON.stringify({
            userMessage: '', // Empty message to trigger bot greeting
            sessionId: sessionId,
          }),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        // Read the streaming response to completion
        const reader = response.body?.getReader()
        if (reader) {
          const decoder = new TextDecoder()
          let buffer = ''

          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''

            for (const line of lines) {
              if (line.trim()) {
                try {
                  const data = JSON.parse(line)
                  console.log('Received streaming data from empty message:', data)
                } catch (e) {
                  console.error('Error parsing empty message streaming response:', e)
                }
              }
            }
          }
        }

        // After the empty message is processed, refetch messages to get the bot's greeting
        console.log('Empty message sent successfully, refetching messages...')
        setTimeout(() => {
          refetchMessages()
        }, 500) // Small delay to ensure backend has processed the message
      } catch (error) {
        console.error('Error sending empty message to bot:', error)
        // Add a fallback greeting message
        const greetingMessage: Message = {
          id: `greeting-${Date.now()}`,
          sender: 'bot',
          content: 'Hello! How can I help you today?',
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, greetingMessage])
      }
    },
    [refetchMessages]
  )

  // Handle new conversation
  const handleNewConversation = useCallback(() => {
    if (!user?.id) return

    // Create a new GeneralChat session
    createFlowChatSessionMutate(
      {
        input: {
          flow: OnboardingFlowType.GeneralChat,
        },
      },
      {
        onSuccess: async (response: any) => {
          const newSessionId = response.createFlowChatSession.id
          console.log('Created new GeneralChat session:', newSessionId)

          // Set the session ID and reset state
          setCurrentSessionId(newSessionId)
          setMessages([]) // Clear messages, bot greeting will come from empty message
          setShowSessionList(false)
          setUserMessageCount(0)
          setIsUserScrolling(false)

          // Send empty message to trigger bot greeting
          await sendEmptyMessageToBot(newSessionId)
        },
        onError: (error: any) => {
          console.error('Error creating new chat session:', error)
          // Still close the session list and show error
          setShowSessionList(false)
        },
      }
    )
  }, [user?.id, createFlowChatSessionMutate, sendEmptyMessageToBot])

  // Handle suggestion click
  const handleSuggestionClick = useCallback(
    async (suggestion: string) => {
      if (!user?.id || isTyping) return

      // Set the suggestion as the input value
      setInputValue(suggestion)

      // Create user message
      const userMessage: Message = {
        id: `user-${Date.now()}`,
        sender: 'user',
        content: suggestion,
        timestamp: new Date(),
      }

      setMessages((prev) => [...prev, userMessage])
      setIsTyping(true)

      // Increment user message count
      const newMessageCount = userMessageCount + 1
      setUserMessageCount(newMessageCount)

      try {
        // Call the streaming Lambda Function URL
        const streamChatbotUrl = process.env.REACT_APP_STREAM_CHATBOT_URL || ''
        const authToken = await getAuthToken()

        if (!authToken) {
          throw new Error('Authentication token is required')
        }

        const response = await fetch(streamChatbotUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken,
          },
          body: JSON.stringify({
            userMessage: suggestion,
            sessionId: currentSessionId,
          }),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('No response body reader available')
        }

        let botMessage: Message = {
          id: `bot-${Date.now()}`,
          sender: 'bot',
          content: '',
          timestamp: new Date(),
        }

        // Add the empty bot message immediately
        setMessages((prev) => [...prev, botMessage])

        const decoder = new TextDecoder()
        let buffer = ''
        let fullContent = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.trim()) {
              try {
                const data: StreamingResponse = JSON.parse(line)

                if (data.type === 'session') {
                  setCurrentSessionId(data.sessionId || null)
                } else if (data.type === 'content') {
                  fullContent += data.content || ''
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === botMessage.id ? { ...msg, content: fullContent } : msg
                    )
                  )
                } else if (data.type === 'complete') {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === botMessage.id
                        ? { ...msg, content: data.fullResponse || fullContent }
                        : msg
                    )
                  )
                } else if (data.type === 'error') {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === botMessage.id
                        ? {
                            ...msg,
                            content:
                              data.error || 'Sorry, I encountered an error. Please try again.',
                          }
                        : msg
                    )
                  )
                }
              } catch (e) {
                console.error('Error parsing streaming response:', e)
              }
            }
          }
        }
      } catch (error) {
        console.error('Error sending suggestion message:', error)
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          sender: 'bot',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, errorMessage])
      } finally {
        setIsTyping(false)
        setInputValue('') // Clear the input
      }
    },
    [user?.id, isTyping, currentSessionId, userMessageCount]
  )

  // Auto-generate title after 4 user messages (only once per session)
  const checkAndGenerateTitle = useCallback(
    (sessionId: string, messageCount: number) => {
      if (messageCount <= 6 && sessionId) {
        summarySessionTitleMutate({ id: sessionId })
        // Mark this session as titled to prevent future calls
      }
    },
    [summarySessionTitleMutate]
  )

  // Group sessions by date
  const groupSessionsByDate = useCallback((sessions: any[]) => {
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    const groups: Record<string, any[]> = {}

    sessions.forEach((session) => {
      if (!session.createdAt) return

      const sessionDate = new Date(session.createdAt)
      const sessionDateString = sessionDate.toDateString()
      const todayString = today.toDateString()
      const yesterdayString = yesterday.toDateString()

      let groupKey: string
      if (sessionDateString === todayString) {
        groupKey = 'Today'
      } else if (sessionDateString === yesterdayString) {
        groupKey = 'Yesterday'
      } else {
        // Format as "Apr 26, 2025"
        groupKey = sessionDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      }

      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(session)
    })

    return groups
  }, [])

  // Filter sessions based on search query
  const filteredSessions = React.useMemo(() => {
    const sessions = chatSessionsData?.getChatSessions || []
    if (!searchQuery.trim()) {
      return sessions
    }
    return sessions.filter((session: any) =>
      (session.title || 'General Chat').toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [chatSessionsData?.getChatSessions, searchQuery])

  // Group filtered sessions by date
  const groupedSessions = React.useMemo(() => {
    return groupSessionsByDate(filteredSessions)
  }, [filteredSessions, groupSessionsByDate])

  // Close chat when clicking outside - disabled to allow modal interactions
  // useEffect(() => {
  //   const handleClickOutside = (event: MouseEvent) => {
  //     if (floatingChatRef.current && !floatingChatRef.current.contains(event.target as Node)) {
  //       if (isOpen) {
  //         toggleChat()
  //       }
  //     }
  //   }

  //   if (isOpen) {
  //     document.addEventListener('mousedown', handleClickOutside)
  //   }

  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside)
  //   }
  // }, [isOpen, toggleChat])

  // On Send, for each pending file, dispatch uploadFileAction (no callback, no Promise)
  const handleSendMessage = useCallback(() => {
    if (isTyping || processing || !user?.id) return
    const messageToSend = inputValue.trim()

    // If any file is uploading, do nothing
    if (pendingFiles.some((pf) => pf.status === 'uploading')) return

    // If there are files with status 'pending', start upload
    if (pendingFiles.some((pf) => pf.status === 'pending')) {
      const idx = pendingFiles.findIndex((pf) => pf.status === 'pending')
      if (idx !== -1) {
        const newPendingFiles = [...pendingFiles]
        newPendingFiles[idx] = { ...newPendingFiles[idx], status: 'uploading' }
        setPendingFiles(newPendingFiles)
        uploadFileAction({
          files: [newPendingFiles[idx].file],
          isMultiple: false,
          saveFile: true,
        })
      }
      return
    }

    // If all files are uploaded or no files, send the message
    const fileLinks = pendingFiles
      .filter((pf) => pf.status === 'uploaded' && pf.uploadedUrl && pf.uploadedName)
      .map((pf) => `[${pf.uploadedName}](${pf.uploadedUrl})`)
      .join('\n')
    let finalMessage = messageToSend
    if (fileLinks) {
      finalMessage = finalMessage ? `${finalMessage}\n${fileLinks}` : fileLinks
    }

    // Add user message to UI immediately
    setMessages((prev) => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        sender: 'user',
        content: finalMessage,
        timestamp: new Date(),
      },
    ])
    setInputValue('')
    setPendingFiles([])
    setProcessing(true)

    // Use streaming endpoint for all messages (handles both text and structured responses)
    ;(async () => {
      try {
        const streamChatbotUrl = process.env.REACT_APP_STREAM_CHATBOT_URL || ''
        const authToken = await getAuthToken()
        if (!authToken) throw new Error('Authentication token is required')

        const response = await fetch(streamChatbotUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken,
          },
          body: JSON.stringify({
            userMessage: finalMessage,
            userMessageType: fileLinks ? 'file' : 'text',
            sessionId: currentSessionId,
          }),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('No response body reader available')
        }

        let botMessage: Message = {
          id: `bot-${Date.now()}`,
          sender: 'bot',
          content: '',
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, botMessage])

        const decoder = new TextDecoder()
        let buffer = ''
        let fullContent = ''
        let isStructuredResponse = false
        let structuredContent: any = null

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.trim()) {
              try {
                const data: StreamingResponse = JSON.parse(line)

                if (data.type === 'session') {
                  setCurrentSessionId(data.sessionId || null)
                  if (data.sessionId) {
                    checkAndGenerateTitle(data.sessionId, userMessageCount + 1)
                  }
                } else if (data.type === 'content') {
                  // Check if content is structured (JSON) or plain text
                  const content = data.content || ''

                  // Try to parse as JSON first (structured response)
                  try {
                    const parsedContent = JSON.parse(content)
                    if (parsedContent.content && typeof parsedContent.content === 'object') {
                      // This is a structured response (form/UI component)
                      isStructuredResponse = true
                      structuredContent = parsedContent
                      setMessages((prev) =>
                        prev.map((msg) =>
                          msg.id === botMessage.id
                            ? { ...msg, content: parsedContent.content }
                            : msg
                        )
                      )
                    } else {
                      // Parsed but not structured, treat as text
                      fullContent += content
                      setMessages((prev) =>
                        prev.map((msg) =>
                          msg.id === botMessage.id ? { ...msg, content: fullContent } : msg
                        )
                      )
                    }
                  } catch (parseError) {
                    // Not JSON, treat as regular text content
                    fullContent += content
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === botMessage.id ? { ...msg, content: fullContent } : msg
                      )
                    )
                  }
                } else if (data.type === 'complete') {
                  if (isStructuredResponse && structuredContent) {
                    // Keep the structured content
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === botMessage.id
                          ? { ...msg, content: structuredContent.content }
                          : msg
                      )
                    )
                  } else {
                    // Use the complete response or accumulated content
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === botMessage.id
                          ? { ...msg, content: data.fullResponse || fullContent }
                          : msg
                      )
                    )
                  }
                  
                  // Handle TTS with transcript if available
                  if (ttsSupported) {
                    const finalContent = isStructuredResponse && structuredContent 
                      ? structuredContent.content 
                      : data.fullResponse || fullContent
                    
                    // Use transcript from response if available (for forms), otherwise extract text from content
                    const textToSpeak = data.transcript || extractTextForTTS(finalContent)
                    
                    if (textToSpeak) {
                      speakText(textToSpeak)
                    }
                  }
                } else if (data.type === 'error') {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === botMessage.id
                        ? {
                            ...msg,
                            content:
                              data.error || 'Sorry, I encountered an error. Please try again.',
                          }
                        : msg
                    )
                  )
                }
              } catch (e) {
                console.error('Error parsing streaming response:', e, 'Line:', line)
              }
            }
          }
        }

        // Update user message count after successful response
        setUserMessageCount(userMessageCount + 1)
      } catch (error) {
        console.error('Error sending message:', error)
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          sender: 'bot',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, errorMessage])
      } finally {
        setProcessing(false)
      }
    })()
  }, [
    checkAndGenerateTitle,
    currentSessionId,
    inputValue,
    isTyping,
    processing,
    user?.id,
    userMessageCount,
    pendingFiles,
    uploadFileAction,
    speakText,
    ttsSupported,
  ])

  // useEffect to watch uploadedFile from Redux and update pendingFiles
  useEffect(() => {
    if (uploadedFile && pendingFiles.some((pf) => pf.status === 'uploading' && !pf.uploadedUrl)) {
      // Find the uploading file and update it
      setPendingFiles((prev) =>
        prev.map((pf) =>
          pf.status === 'uploading' && !pf.uploadedUrl
            ? {
                ...pf,
                status: 'uploaded',
                uploadedUrl: uploadedFile.url,
                uploadedName: uploadedFile.name,
              }
            : pf
        )
      )
    }
  }, [uploadedFile, pendingFiles])

  // Custom file link renderer for Markdown
  const isImage = (url: string) => /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(url)

  const FileLinkRenderer = (props: any) => {
    const { href, children } = props
    const filename = children[0]
    if (isImage(href)) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-center m-1"
        >
          <img src={href} alt={filename} className="max-w-[120px] max-h-[120px] rounded mb-1" />
          <div className="text-xs text-white">{filename}</div>
        </a>
      )
    }
    // For non-image files, show a styled file link with icon and white text using Tailwind
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 bg-white/10 mt-3 border border-white/25 rounded-lg px-3 py-1.5 text-white font-medium no-underline my-1 text-sm transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
      >
        <span role="img" aria-label="file" className="text-lg">
          📄
        </span>
        <span>{filename}</span>
      </a>
    )
  }

  // Custom paragraph renderer for Markdown to ensure white text
  const WhiteParagraph = (props: any) => <p className="text-white">{props.children}</p>

  // Form submission handler using streaming endpoint
  const submitDataConfirmationForm = useCallback(
    async (formData: any) => {
      const filesFields = ['businessPlanFile']
      const filesToUpload = filesFields.filter((field) => formData[field])

      if (filesToUpload.length > 0) {
        filesToUpload.forEach((field) => {
          // Check if formData[field] is an array
          const files = Array.isArray(formData[field]) ? formData[field] : [formData[field]]
          uploadFileAction({
            files: files as unknown as FileList,
            isMultiple: files.length > 1, // Set isMultiple based on file count
            saveFile: true,
          })
        })
        return
      }

      setProcessing(true)
      const { id, ...data } = formData

      // Add user form response message to UI immediately

      // Use streaming endpoint for form submission
      ;(async () => {
        try {
          const streamChatbotUrl = process.env.REACT_APP_STREAM_CHATBOT_URL || ''
          const authToken = await getAuthToken()
          if (!authToken) throw new Error('Authentication token is required')

          const response = await fetch(streamChatbotUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: authToken,
            },
            body: JSON.stringify({
              userMessage: JSON.stringify(data),
              userMessageType: 'formResponse',
              ...(formData.skip ? { skip: true } : {}),
              sessionId: currentSessionId,
            }),
          })

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const reader = response.body?.getReader()
          if (!reader) {
            throw new Error('No response body reader available')
          }

          let botMessage: Message = {
            id: `bot-${Date.now()}`,
            sender: 'bot',
            content: '',
            timestamp: new Date(),
          }
          setMessages((prev) => [...prev, botMessage])

          const decoder = new TextDecoder()
          let buffer = ''
          let fullContent = ''
          let isStructuredResponse = false
          let structuredContent: any = null

          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''

            for (const line of lines) {
              if (line.trim()) {
                try {
                  const streamData: StreamingResponse = JSON.parse(line)

                  if (streamData.type === 'session') {
                    setCurrentSessionId(streamData.sessionId || null)
                  } else if (streamData.type === 'content') {
                    // Check if content is structured (JSON) or plain text
                    const content = streamData.content || ''

                    // Try to parse as JSON first (structured response)
                    try {
                      const parsedContent = JSON.parse(content)
                      if (parsedContent.content && typeof parsedContent.content === 'object') {
                        // This is a structured response (form/UI component)
                        isStructuredResponse = true
                        structuredContent = parsedContent
                        setMessages((prev) =>
                          prev.map((msg) =>
                            msg.id === botMessage.id
                              ? { ...msg, content: parsedContent.content }
                              : msg
                          )
                        )
                      } else {
                        // Parsed but not structured, treat as text
                        fullContent += content
                        setMessages((prev) =>
                          prev.map((msg) =>
                            msg.id === botMessage.id ? { ...msg, content: fullContent } : msg
                          )
                        )
                      }
                    } catch (parseError) {
                      // Not JSON, treat as regular text content
                      fullContent += content
                      setMessages((prev) =>
                        prev.map((msg) =>
                          msg.id === botMessage.id ? { ...msg, content: fullContent } : msg
                        )
                      )
                    }
                  } else if (streamData.type === 'complete') {
                    if (isStructuredResponse && structuredContent) {
                      // Keep the structured content
                      setMessages((prev) =>
                        prev.map((msg) =>
                          msg.id === botMessage.id
                            ? { ...msg, content: structuredContent.content }
                            : msg
                        )
                      )
                    } else {
                      // Use the complete response or accumulated content
                      setMessages((prev) =>
                        prev.map((msg) =>
                          msg.id === botMessage.id
                            ? { ...msg, content: streamData.fullResponse || fullContent }
                            : msg
                        )
                      )
                    }
                  } else if (streamData.type === 'error') {
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === botMessage.id
                          ? {
                              ...msg,
                              content:
                                streamData.error ||
                                'Sorry, I encountered an error. Please try again.',
                            }
                          : msg
                      )
                    )
                  }
                } catch (e) {
                  console.error('Error parsing form response stream:', e, 'Line:', line)
                }
              }
            }
          }
        } catch (error) {
          console.error('Error sending form data:', error)
          const errorMessage: Message = {
            id: `error-${Date.now()}`,
            sender: 'bot',
            content: 'Sorry, I encountered an error processing your form. Please try again.',
            timestamp: new Date(),
          }
          setMessages((prev) => [...prev, errorMessage])
        } finally {
          setProcessing(false)
        }
      })()
    },
    [currentSessionId, uploadFileAction]
  )

  // Handle audio recording completion
  const handleAudioRecording = useCallback(async (audioBlob: Blob, transcript?: string, duration?: number) => {
    
    if (!audioBlob || isTyping || processing || !user?.id) return
    
    // Check if we have a transcript
    if (!transcript || transcript.trim() === '') {
      // Show user-friendly message and allow manual input
      const userInput = prompt(errorMessages.speechRecognitionFailed)
      if (!userInput || userInput.trim() === '') {
        return
      }
      transcript = userInput.trim()
    }
    
    try {
      // Determine if this should be treated as form input
      const lastMessageContent = messages[messages.length - 1]?.content
      const isFormContext = typeof lastMessageContent === 'object' && 
        lastMessageContent?.type === OnboardingFlowStepTypes.Form
      
      const messageType = isFormContext ? 'AudioToForm' : 'Audio'
      
      // Add user message to UI immediately with voice metadata
      const userMessage: Message = {
        id: `user-${Date.now()}`,
        sender: 'user',
        content: transcript.trim(),
        timestamp: new Date(),
        isVoiceMessage: true,
        voiceDuration: duration || 0,
      }
      
      setMessages((prev) => [...prev, userMessage])
      setProcessing(true)
      
      // Send via streaming endpoint
      const streamChatbotUrl = process.env.REACT_APP_STREAM_CHATBOT_URL || ''
      const authToken = await getAuthToken()
      if (!authToken) throw new Error('Authentication token is required')
      
      const response = await fetch(streamChatbotUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
        body: JSON.stringify({
          userMessage: transcript.trim(),
          userMessageType: messageType,
          audioTranscript: transcript.trim(),
          sessionId: currentSessionId,
          isVoiceMessage: true,
          voiceDuration: duration || 0,
        }),
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      // Handle streaming response (same as text messages)
      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No response body reader available')
      }
      
      let botMessage: Message = {
        id: `bot-${Date.now()}`,
        sender: 'bot',
        content: '',
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, botMessage])
      
      const decoder = new TextDecoder()
      let buffer = ''
      let fullContent = ''
      let isStructuredResponse = false
      let structuredContent: any = null
      // let responseTranscript = '' // Removed unused variable
      
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        
        for (const line of lines) {
          if (line.trim()) {
            try {
              const data = JSON.parse(line)
              
              if (data.type === 'session') {
                setCurrentSessionId(data.sessionId || null)
              } else if (data.type === 'content') {
                const content = data.content || ''
                
                try {
                  const parsedContent = JSON.parse(content)
                  if (parsedContent.content && typeof parsedContent.content === 'object') {
                    isStructuredResponse = true
                    structuredContent = parsedContent
                    // responseTranscript = parsedContent.transcript || '' // Removed unused assignment
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === botMessage.id 
                          ? { ...msg, content: parsedContent.content } 
                          : msg
                      )
                    )
                  } else {
                    fullContent += content
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === botMessage.id ? { ...msg, content: fullContent } : msg
                      )
                    )
                  }
                } catch {
                  fullContent += content
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === botMessage.id ? { ...msg, content: fullContent } : msg
                    )
                  )
                }
              } else if (data.type === 'complete') {
                if (isStructuredResponse && structuredContent) {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === botMessage.id
                        ? { ...msg, content: structuredContent.content }
                        : msg
                    )
                  )
                } else {
                  const finalContent = data.fullResponse || fullContent
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === botMessage.id
                        ? { ...msg, content: finalContent }
                        : msg
                    )
                  )
                  
                  // Auto-play TTS for text responses
                  if (ttsSupported && finalContent) {
                    const textToSpeak = extractTextForTTS(finalContent)
                    if (textToSpeak) {
                      speakText(textToSpeak)
                    }
                  }
                }
              }
            } catch (e) {
              console.error('Error parsing audio response stream:', e)
            }
          }
        }
      }
      
      setUserMessageCount(userMessageCount + 1)
    } catch (error) {
      console.error('Error sending audio message:', error)
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        sender: 'bot',
        content: errorMessages.audioErrorMessage,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setProcessing(false)
    }
  }, [
    currentSessionId,
    isTyping,
    processing,
    user?.id,
    userMessageCount,
    messages,
    ttsSupported,
    speakText,
  ])

  // Handle new message from bot response
  const handleNewMessage = useCallback((message: any) => {
    setCurrentStep(message)
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        id: `bot-${Date.now()}`,
        sender: 'bot',
        content: message.content,
        timestamp: new Date(),
      },
    ])
    setIsUserScrolling(false)
    setProcessing(false)
    
    // Auto-play TTS for bot messages if supported
    if (ttsSupported && message.content) {
      const textToSpeak = extractTextForTTS(message.content)
      if (textToSpeak) {
        speakText(textToSpeak)
      }
    }
  }, [ttsSupported, speakText])

  // Handle typing animation callback
  const handleTypingMessage = useCallback(
    (text: string, displayText: string) => {
      if (text === displayText) {
        if (queuedMessages.length && text) {
          const firstQueueMessage = queuedMessages.shift()
          handleNewMessage(firstQueueMessage!)
          setQueuedMessages([...queuedMessages])
          return
        }
      } else {
        if (text?.length) {
        }
      }
      if (displayText.length) {
        scrollToBottom()
      }
    },
    [queuedMessages, handleNewMessage, scrollToBottom]
  )

  // Handle created user task for play creation success/error tracking
  const handleSetCreatedUserTask: React.Dispatch<React.SetStateAction<UserTask>> = (value) => {
    if (typeof value === 'function') {
      setCreatedUserTask((prev) => value(prev!))
    } else {
      setCreatedUserTask(value)
    }
  }

  // Note: File upload for forms is now handled through the form submission flow

  useEffect(() => {
    if (uploadedFormFile && uploadedFormFile.length > 0 && startUpload) {
      setValue(
        'businessPlanFile',
        uploadedFormFile.length === 1 ? uploadedFormFile[0] : uploadedFormFile
      )
      handleSubmit((formData) => submitDataConfirmationForm({ ...formData, skip: 'true' }))()
      setUploadedFormFile([])
      setStartUpload(false)
      resetField('businessPlanFile')
    }
  }, [
    startUpload,
    uploadedFormFile,
    setValue,
    handleSubmit,
    submitDataConfirmationForm,
    resetField,
  ])

  // Check if current step requires form input
  const lastMessageContent = messages[messages.length - 1]?.content
  const isFormStep =
    currentStep.stepType === OnboardingFlowStepTypes.Form ||
    (typeof lastMessageContent === 'object' &&
      lastMessageContent?.type === OnboardingFlowStepTypes.Form)
  const isToolConnectionStep =
    currentStep.stepType === OnboardingFlowStepTypes.ToolConnection ||
    (typeof lastMessageContent === 'object' &&
      lastMessageContent?.type === OnboardingFlowStepTypes.ToolConnection)

  return (
    <div
      ref={floatingChatRef}
      className={`fixed z-50 ${isExpanded ? 'inset-0' : 'bottom-4 right-4'}`}
    >
      {/* Backdrop when expanded */}
      {isOpen && isExpanded && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={toggleExpand} />
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`bg-white rounded-lg flex flex-col transition-all duration-300 ${
            isExpanded
              ? 'w-[100vw] h-[98vh] fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50'
              : 'w-[75vw] h-[75vh] mb-2'
          }`}
          style={{
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          }}
        >
          {/* Header */}
          <div className="rounded-t-lg bg-app-primary-125">
            <div className="flex items-center justify-between p-4 text-white">
              <div className="flex flex-col">
                <div className="font-semibold text-white text-base">Messages</div>
                <div className="text-xs text-white opacity-90">Choose who to chat with</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleFolderClick}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                  title="View chat sessions"
                >
                  <FolderIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={toggleExpand}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                  title={isExpanded ? 'Minimize chat' : 'Expand chat'}
                >
                  <ArrowsPointingInIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={toggleChat}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Tabs - Grey background section */}
          <div className="px-4 py-3 border-b bg-app-grey-5 border-app-grey-25">
            <div className="flex bg-white rounded-lg p-1 shadow-sm">
              <button
                onClick={() => setActiveTab('coach')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'coach'
                    ? 'font-semibold bg-app-white-95 text-app-grey-100'
                    : 'hover:opacity-80 bg-transparent text-app-grey-65'
                }`}
              >
                <AppAvatar size="xs" type="image" shape="Rounded" src={Avatar} />
                Ask Coach Logic
              </button>
              <button
                onClick={() => setActiveTab('chat')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'chat'
                    ? 'font-semibold bg-app-white-95 text-app-grey-100'
                    : 'hover:opacity-80 bg-transparent text-app-grey-65'
                }`}
              >
                Chat
              </button>
            </div>
          </div>

          {/* Session List */}
          {showSessionList && (
            <div className="border-b bg-white border-app-grey-25">
              <div className="p-4 max-h-80 overflow-y-auto">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-800">Chat history</h4>
                  <button
                    onClick={handleNewConversation}
                    className="px-3 py-1 text-sm rounded-md transition-colors bg-app-primary-125 text-white hover:bg-app-primary-150"
                  >
                    New Chat
                  </button>
                </div>

                {/* Search Input */}
                <div className="relative mb-4">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-4 w-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search chat history"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-125 focus:border-transparent"
                  />
                </div>

                {/* Grouped Sessions */}
                <div className="space-y-4">
                  {Object.keys(groupedSessions).length > 0 ? (
                    Object.entries(groupedSessions).map(([dateGroup, sessions]) => (
                      <div key={dateGroup}>
                        <h5 className="text-sm font-medium text-gray-700 mb-2">{dateGroup}</h5>
                        <div className="space-y-1">
                          {sessions.map((session) => (
                            <button
                              key={session.id}
                              onClick={() => handleSessionSelect(session)}
                              className={`w-full text-left p-3 rounded-lg transition-colors hover:bg-gray-50 ${
                                currentSessionId === session.id
                                  ? 'bg-app-primary-25 border border-app-primary-125'
                                  : 'bg-transparent hover:bg-gray-50'
                              }`}
                            >
                              <div className="font-medium text-sm text-gray-800 truncate mb-1">
                                {session.title || 'General Chat'}
                              </div>
                              {session.lastMessage && (
                                <div className="text-xs text-gray-600 truncate">
                                  {session.lastMessage.content}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 text-sm py-8">
                      {searchQuery ? 'No matching conversations found' : 'No chat sessions found'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'coach' ? (
              /* Ask Coach Logic Tab */
              <>
                {/* Loading state for fetching messages */}
                {isMessagesLoading && currentSessionId && (
                  <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                    <div className="mb-4">
                      <LoadingIndicator size={40} />
                    </div>
                    <p className="text-gray-600 text-sm">Loading conversation...</p>
                  </div>
                )}
                {/* Welcome screen for new conversations */}
                {!isMessagesLoading && messages.length === 1 && !currentSessionId ? (
                  /* Initial welcome screen */
                  <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                    <div className="mb-6">
                      <AppAvatar size="xl" type="image" shape="Rounded" src={Avatar} />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Coach Logic</h2>
                    <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                      Hi, I'm here 24/7 to answer any business related questions, research, or
                      analysis to help you support your clients and help you win.
                    </p>
                    <button
                      className="px-6 py-3 text-white rounded-lg font-medium transition-colors bg-app-primary-125"
                      onClick={() => {
                        if (!user?.id || isTyping) return

                        // Create a new GeneralChat session first
                        createFlowChatSessionMutate(
                          {
                            input: {
                              flow: OnboardingFlowType.GeneralChat,
                            },
                          },
                          {
                            onSuccess: async (response: any) => {
                              const newSessionId = response.createFlowChatSession.id
                              console.log('Created new GeneralChat session:', newSessionId)

                              // Set the session ID
                              setCurrentSessionId(newSessionId)

                              // Send empty message to trigger bot greeting
                              await sendEmptyMessageToBot(newSessionId)
                            },
                            onError: (error: any) => {
                              console.error('Error creating chat session:', error)
                              const errorMessage: Message = {
                                id: `error-${Date.now()}`,
                                sender: 'bot',
                                content:
                                  'Sorry, I encountered an error creating the chat session. Please try again.',
                                timestamp: new Date(),
                              }
                              setMessages((prev) => [...prev, errorMessage])
                            },
                          }
                        )
                      }}
                      disabled={isTyping || !user?.id}
                    >
                      Start Conversation
                    </button>
                  </div>
                ) : /* Chat messages - only show when not loading and have messages or session */
                !isMessagesLoading && (messages.length > 1 || currentSessionId) ? (
                  <div
                    className={`bg-app-grey-5 flex flex-col w-full p-2 sm:p-4 pt-0 overflow-y-scroll gap-8 ${
                      isExpanded ? 'h-[70vh]' : 'h-[100%]'
                    }`}
                    ref={chatContainerRef}
                    onScroll={handleUserScroll}
                  >
                    {isFetchingNextPage && (
                      <div className="flex justify-center p-1">
                        <LoadingIndicator size={20} />
                      </div>
                    )}
                    {messages
                      .filter((message) => {
                        // Filter out empty messages for both string and object content
                        if (typeof message.content === 'string') {
                          return message.content && message.content.trim() !== ''
                        }
                        return message.content // Objects are always considered valid
                      })
                      .map((message, index) => (
                        <React.Fragment key={message.id}>
                          <div
                            className={`flex gap-2 ${
                              message.sender === 'user' ? 'justify-end' : 'justify-start'
                            }`}
                          >
                            <div className={`${message.sender === 'user' ? 'order-2' : ''}`}>
                              <AppAvatar
                                size={'md'}
                                type={message.sender === 'user' && !logo ? 'text' : 'image'}
                                shape={'Rounded'}
                                src={message.sender === 'user' ? logo : Avatar}
                                text={
                                  message.sender === 'user' && !logo
                                    ? getInitials({
                                        firstName: user?.firstName || '',
                                        lastName: user?.lastName || '',
                                      })
                                    : undefined
                                }
                                color="Primary"
                              />
                            </div>
                            {/* Render form content using AppDynamicChatMessage */}
                            {typeof message.content === 'object' && message.sender === 'bot' ? (
                              <div className="flex-1">
                                <AppDynamicChatMessage
                                  content={message.content as UIComponentProps}
                                  onSubmit={submitDataConfirmationForm}
                                  typingAnimationCallback={handleTypingMessage}
                                  useTypingAnimation={index === messages.length - 1}
                                  isLastMessage={index === messages.length - 1}
                                  isSendingMessageTobot={processing}
                                  tools={tools}
                                  setIsUpload={setIsUpload}
                                  showUploadModal={showUploadModal}
                                  isUpload={isUpload}
                                  uploadedFile={uploadedFormFile}
                                  setUploadedFile={setUploadedFormFile}
                                  startUpload={startUpload}
                                  setStartUpload={setStartUpload}
                                  scrollToBottom={scrollToBottom}
                                  refetchBusinessGoal={() => {}} // Not needed for general chat
                                  refetchTool={refetchTools}
                                  addMessage={(msg: any) => {
                                    setMessages((prev) => [
                                      ...prev,
                                      {
                                        id: `user-${Date.now()}`,
                                        sender: 'user',
                                        content: msg.text || msg.content,
                                        timestamp: new Date(),
                                      },
                                    ])
                                  }}
                                />
                              </div>
                            ) : (
                              <div className="flex justify-start">
                                {message.sender === 'user' && message.isVoiceMessage ? (
                                  <AppVoiceIndicator
                                    transcript={typeof message.content === 'string' ? message.content : String(message.content)}
                                    duration={message.voiceDuration}
                                    className="max-w-[90%]"
                                  />
                                ) : (
                                <div
                                  className={`w-fit max-w-[90%] flex rounded-lg px-3 pt-2 pb-1.5 text-base !font-inter relative ${
                                    message.sender === 'bot'
                                      ? 'bg-app-grey-20 text-app-grey-100'
                                      : 'text-white bg-app-primary-125'
                                  }`}
                                >
                                   {message.sender === 'bot' ? (
                                  <>
                                  <SafeMarkdown
                                    className="chatbot-markdown"
                                    components={{ a: FileLinkRenderer }}
                                  >
                                    {typeof message.content === 'string'
                                      ? message.content
                                      : String(message.content)}
                                  </SafeMarkdown>
                                  {ttsSupported && typeof message.content === 'string' && (
                                    <div className="flex justify-end ml-2">
                                      <AppTextToSpeech
                                        text={extractTextForTTS(message.content)}
                                        disabled={false}
                                        className="opacity-70 hover:opacity-100"
                                        onStart={() => {}}
                                        onEnd={() => {}}
                                        onError={() => {}}
                                      />
                                    </div>
                                  )}
                                  </>
                                ) : (
                                  <SafeMarkdown
                                    className="whitespace-pre-wrap text-white"
                                    components={{ a: FileLinkRenderer, p: WhiteParagraph }}
                                  >
                                    {typeof message.content === 'string'
                                      ? message.content
                                      : String(message.content)}
                                  </SafeMarkdown>
                                  )}
                                </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Show suggestions after the first bot message */}
                          {message.sender === 'bot' && index === 0 && userMessageCount === 0 && (
                            <div className="flex justify-start">
                              <div className="ml-12 max-w-[90%]">
                                <div className="text-sm text-gray-700 font-medium mb-3">
                                  Quick suggestions:
                                </div>
                                <div className="space-y-2">
                                  {[
                                    'Do some research or analysis for me',
                                    'Give me feedback on a document I created',
                                    'Report an issue/problem with GoTackle',
                                    'Recommend a tech tool (Tech Tools Command Center)',
                                    'Suggest an improvement/ Request a feature',
                                  ].map((suggestion, suggestionIndex) => (
                                    <button
                                      key={suggestionIndex}
                                      onClick={() => handleSuggestionClick(suggestion)}
                                      className="w-full text-left p-3 rounded-lg bg-app-white-95 hover:bg-app-grey-10 transition-colors text-sm text-app-grey-100 border border-app-grey-25 shadow-sm"
                                      disabled={isTyping || !user?.id}
                                    >
                                      {suggestion}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      ))}

                    {isTyping && (
                      <div className="flex gap-2">
                        <AppTypingIndicator />
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>
                ) : null}
              </>
            ) : (
              /* Chat Tab - Stream Chat Integration */
              <div className="flex flex-col h-full">
                {user && token ? (
                  <Chat
                    user={user}
                    token={token}
                    chatUsers={chatUsers}
                    chatUnreadMessageCount={chatUnreadMessageCount}
                    usersAssessmentResponseByQuestion={usersAssessmentResponseByQuestion}
                    saveUserFileAction={saveUserFileAction}
                    getChatUsersAction={getChatUsersAction}
                    chatUnreadMessageCountAction={chatUnreadMessageCountAction}
                    getUsersAssessmentResponseByQuestionAction={
                      getUsersAssessmentResponseByQuestionAction
                    }
                  />
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                    <div className="mb-4">
                      <svg
                        className="w-16 h-16 text-gray-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h8z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Loading Chat...</h3>
                    <p className="text-gray-500 text-sm">
                      Please wait while we load your team chat
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Input - Only show when in Coach tab and conversation has started */}
          {activeTab === 'coach' && (messages.length > 1 || currentSessionId) && (
            <div className="p-4 border-gray-100">
              <div className="flex flex-col rounded-lg px-4 py-3 border border-primary-appDarkBorder">
                {/* File preview UI */}
                {pendingFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {pendingFiles.map((pf, idx) => (
                      <div
                        key={pf.file.name + idx}
                        className="flex items-center gap-2 bg-app-grey-10 rounded p-2"
                      >
                        {pf.previewUrl ? (
                          <img
                            src={pf.previewUrl}
                            alt={pf.file.name}
                            className="w-10 h-10 object-cover rounded"
                          />
                        ) : (
                          <span className="w-10 h-10 flex items-center justify-center bg-app-grey-20 rounded text-xs">
                            {pf.file.name.split('.').pop()?.toUpperCase()}
                          </span>
                        )}
                        <span className="text-xs max-w-[100px] truncate">{pf.file.name}</span>
                        {pf.status === 'uploading' && (
                          <CircleNotchIcon className="animate-spin w-4 h-4 ml-1" />
                        )}
                        {pf.status === 'uploaded' && <span className="text-green-500 ml-1">✓</span>}
                        {pf.status !== 'uploading' && (
                          <button
                            className="ml-1 text-red-500 hover:text-red-700"
                            onClick={() => {
                              setPendingFiles(pendingFiles.filter((_, i) => i !== idx))
                            }}
                            title="Remove file"
                            type="button"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* Textarea and send button */}
                <textarea
                  disabled={isTyping || processing || isFormStep || isToolConnectionStep}
                  rows={3}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Message Coach Logic..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
                      e.preventDefault()
                      handleSendMessage()
                    } else if (
                      (e.key === 'Enter' && e.shiftKey) ||
                      (e.key === 'Enter' && e.ctrlKey)
                    ) {
                      // Allow line break
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      setInputValue('')
                    } else if (e.key === 'Backspace' && e.ctrlKey) {
                      e.preventDefault()
                      setInputValue((prev) => {
                        // Delete the last word
                        const lastSpaceIndex = prev?.trimEnd().lastIndexOf(' ') ?? -1
                        return lastSpaceIndex !== -1
                          ? prev?.substring(0, lastSpaceIndex + 1) ?? ''
                          : ''
                      })
                    }
                  }}
                  className="w-full resize-none font-inter text-base text-black-appDark leading-5 bg-transparent border-none focus-visible:ring-0 focus-visible:outline-none"
                />
                <div className="flex justify-between relative w-full">
                  <div className="flex gap-2 items-center">
                    <AppButton
                      variant="clear"
                      size="sm"
                      RightIcon={AppPaperClipIcon}
                      disabled={isTyping}
                      onClick={() => setShowUploadModal(true)}
                    />
                    {/* Audio Recorder */}
                    {audioSupported && (
                      <AppAudioRecorder
                        onRecordingComplete={handleAudioRecording}
                        disabled={isTyping || processing || isToolConnectionStep}
                        maxDuration={120} // 2 minutes
                        className="ml-1"
                      />
                    )}
                    <AppToolTip name="create-play-tooltip" text="Create Play" variant="clear">
                      <AppButton
                        variant="clear"
                        size="sm"
                        RightIcon={AppPlayIcon}
                        disabled={isTyping}
                        onClick={() => setShowCreatePlayModal(true)}
                      />
                    </AppToolTip>
                  </div>

                  {isTyping ? (
                    <CircleNotchIcon className={'fill-primary h-[30px]'} />
                  ) : (
                    <AppButton
                      variant="primary"
                      size="sm"
                      RightIcon={AppSendMessageIcon}
                      disabled={
                        isTyping ||
                        processing ||
                        isFormStep ||
                        isToolConnectionStep ||
                        pendingFiles.some(
                          (pf) => pf.status === 'uploading' || pf.status === 'pending'
                        ) ||
                        (inputValue?.trim() === '' && pendingFiles.length === 0)
                      }
                      onClick={() => handleSendMessage()}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="w-[93px] h-[93px] shadow-lg hover:scale-105 transition-all duration-200 flex items-center justify-center relative"
        >
          <QuickChatIcon className="w-[93px] h-[93px]" />
          {Number(chatUnreadMessageCount) > 0 && (
            <span className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 bg-primary-error text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold z-10 shadow-md">
              {chatUnreadMessageCount}
            </span>
          )}
        </button>
      )}
      {/* Upload Modal */}
      {showUploadModal && (
        <AppModalBox title={'Upload Files'} onClose={() => setShowUploadModal(false)}>
          <div className="m-10">
            <AppFileUploadField
              name="chatFileUpload"
              className="flex-1"
              labelFontSize="base"
              maxFiles={5}
              onChange={(files) => {
                setPendingFiles(
                  files.map((file) => ({
                    file,
                    previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
                    status: 'pending',
                  }))
                )
              }}
            />
            <div className="mt-20 w-[30%]">
              <AppButton
                variant="primary"
                size="sm"
                label="Upload"
                disabled={!pendingFiles || pendingFiles.length === 0 || uploadFileLoading}
                onClick={() => {
                  if (pendingFiles && pendingFiles.length > 0 && user?.id) {
                    uploadFileAction({
                      files: pendingFiles.map((pf) => pf.file),
                      isMultiple: pendingFiles.length > 1,
                      saveFile: true,
                    })
                  }
                  setShowUploadModal(false)
                }}
              />
            </div>
            {uploadFileLoading && (
              <div className="mt-4 text-center text-sm text-gray-500">Uploading...</div>
            )}
          </div>
        </AppModalBox>
      )}

      {/* Create Play Modal */}
      {showCreatePlayModal && (
        <AppModalBox
          Icon={AppCrossIcon}
          height="max-h-[95vh] min-h-[50vh] h-fit"
          width="max-w-2xl"
          titleSize="sm"
          title="Create new play"
          showBorder={false}
          onClose={() => setShowCreatePlayModal(false)}
          onBack={() => setShowCreatePlayModal(false)}
        >
          <AppTask
            tenantId={getTenantId()}
            userId={user?.id || ''}
            source={UserTaskSource.General}
            setShowTaskModal={setShowCreatePlayModal}
            setShowTaskSuccessModal={setShowPlaySuccessModal}
            setShowTaskErrorModal={setShowPlayErrorModal}
            setCreatedUserTask={handleSetCreatedUserTask}
            refetch={() => {}} // We'll handle refetch logic for play board sync later
            logo={logo || ''}
            userName={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || ''}
            advisorsData={advisorsData || []}
            userAvatar={user?.avatar || ''}
            userFirstName={user?.firstName || ''}
            userLastName={user?.lastName || ''}
            goalEndDate=""
            assigneeOptions={assigneeOptions}
            paramUserId={user?.id || ''}
            initialDescription="" // Empty description - no chat context
          />
        </AppModalBox>
      )}

      {/* Play Creation Success/Error Modal */}
      {(showPlaySuccessModal || showPlayErrorModal) && createdUserTask && (
        <AppTaskCreationConfirmation
          showAddTaskSuccessModal={showPlaySuccessModal}
          setShowAddTaskModal={setShowCreatePlayModal}
          setShowAddTaskSuccessModal={setShowPlaySuccessModal}
          setShowAddTaskErrorModal={setShowPlayErrorModal}
          userId={user?.id || ''}
          userAvatar={user?.avatar || ''}
          userFirstName={user?.firstName || ''}
          userLastName={user?.lastName || ''}
          assigneeOptions={assigneeOptions}
          createdUserTask={createdUserTask as UserTask}
        />
      )}
    </div>
  )
}

// Redux connection
const chatProps = (state: RootState) => {
  let { user, chatUsers } = state.user
  let { usersAssessmentResponseByQuestion } = state.owner
  return {
    user,
    chatUsers,
    token: user?.streamAccessToken,
    usersAssessmentResponseByQuestion,
    chatUnreadMessageCount: state.user.chatUnreadMessageCount,
    uploadFileLoading: state.common.uploadFileLoading,
    uploadedFile: state.common.uploadedFile,
  }
}

function chatDispatch(dispatch: Dispatch) {
  return bindActionCreators(
    {
      saveUserFileAction: saveUserFileAction.STARTED,
      getUsersAssessmentResponseByQuestionAction:
        getUsersAssessmentResponseByQuestionAction.STARTED,
      getChatUsersAction: getChatUsersAction.STARTED,
      chatUnreadMessageCountAction: chatUnreadMessageCountAction.STARTED,
      uploadFileAction: require('store/actions/common').uploadFileAction.STARTED,
    },
    dispatch
  )
}

export const AppFloatingChat = connect(chatProps, chatDispatch)(AppFloatingChatComponent)
